import { useMemo, useState } from "react";
import { InputText } from "primereact/inputtext";
import { Skeleton } from "primereact/skeleton";

import { PathCard } from "sj-ui-toolkit/src/atlas/components/ContentCard/PathCard/PathCard";
import {
  PATH_CARD_SMALL_HEIGHT,
  PATH_CARD_SMALL_WIDTH,
} from "sj-ui-toolkit/src/atlas/components/ContentCard/PathCard/PathCard.constants";

import S from "./LearninGrid.module.scss";

const NOUNS = {
  Path: { singular: "path", plural: "paths" },
  Quest: { singular: "quest", plural: "quests" },
};

const skeletonKeys = Array.from({ length: 6 }, (_, i) => `learnin-skelly-${i}`);
// Auto-pack as many cards as fit the container width. `auto-fill` (not `auto-fit`)
// keeps empty tracks so a partial last row leaves blank space on the right rather than
// stretching the few cards across the entire row.
// gridTemplateColumns is derived from PATH_CARD_SMALL_WIDTH so the grid column minimum
// always matches the card width — single source of truth.
const gridStyle = {
  gridTemplateColumns: `repeat(auto-fill, minmax(${PATH_CARD_SMALL_WIDTH}px, 1fr))`,
};

export const LearninGrid = ({
  items = [],
  loading = false,
  variant,
  pathType = "Path",
  title,
  description,
  searchInputId,
  completed = false,
}) => {
  const [search, setSearch] = useState("");
  const nouns = NOUNS[pathType] ?? NOUNS.Path;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(item => {
      const name = (item.name ?? "").toLowerCase();
      const desc = (item.description ?? "").toLowerCase();
      return name.includes(q) || desc.includes(q);
    });
  }, [items, search]);

  const statusMessage = (() => {
    if (loading) return `Loading ${nouns.plural}…`;
    const trimmed = search.trim();
    const count = filtered.length;
    const noun = count === 1 ? nouns.singular : nouns.plural;
    if (count === 0) {
      return trimmed ? `No ${nouns.plural} match "${trimmed}".` : `No ${nouns.plural} available.`;
    }
    return trimmed ? `${count} ${noun} match "${trimmed}".` : `Showing ${count} ${noun}.`;
  })();

  return (
    <>
      <div className="my-6">
        {title ? (
          <h1 className="atlas-h2 flex items-center gap-2">
            {title}
            {completed && (
              <>
                <i
                  className="pi pi-check-circle"
                  style={{ color: "var(--success-color)" }}
                  aria-hidden="true"
                />
                <span className="sr-only">(completed)</span>
              </>
            )}
          </h1>
        ) : (
          <Skeleton width="240px" height="36px" aria-hidden="true" />
        )}
        {description && <p className="body-regular mt-2">{description}</p>}
      </div>

      <div role="search" className="mb-6">
        <label htmlFor={searchInputId} className="sr-only">
          {`Search ${nouns.plural}`}
        </label>
        <div className={S.searchInputWrapper}>
          <InputText
            id={searchInputId}
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={`Search ${nouns.plural} by title or description`}
            className={S.searchInput}
          />
          <span className={S.searchIcon} aria-hidden="true">
            <i className="pi pi-search" />
          </span>
        </div>
      </div>

      <p role="status" aria-live="polite" className="body-regular mb-2">
        {statusMessage}
      </p>

      <div aria-busy={loading} className="grid gap-6 py-6" style={gridStyle}>
        {loading
          ? skeletonKeys.map(key => (
              <Skeleton
                key={key}
                width="100%"
                height={`${PATH_CARD_SMALL_HEIGHT}px`}
                aria-hidden="true"
              />
            ))
          : filtered.map(item => (
              <PathCard
                key={item.id}
                cardSize="small"
                variant={variant}
                id={item.id}
                name={item.name}
                description={item.description}
                pathType={pathType}
                status={item.status}
              />
            ))}
      </div>
    </>
  );
};
